import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

type LogicCategoryOption = { id: string; name?: string };

type LogicCategory = {
  category: string;
  options?: LogicCategoryOption[];
};

type RegimeLike = {
  id?: string | number;
  population?: string | null;
  customLogicIds?: string[];
};

type OptionByCategory = Record<string, string>;

type InitialDraft = {
  population: string | null;
  customLogicIds: string[];
};

type UsePopulationsDraftProps = {
  regime: RegimeLike | null | undefined;
  customLogicCategories?: LogicCategory[];
};

const comparePrimitive = (x: string, y: string) =>
    x < y ? -1 : x > y ? 1 : 0;

const arraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;

  const sorted1 = [...a].sort(comparePrimitive);
  const sorted2 = [...b].sort(comparePrimitive);

  return sorted1.every((v, i) => v === sorted2[i]);
};

const mapLogicIdsToCategories = (
  logicIds: string[],
  categories: LogicCategory[]
) => {
  const categoryIds: string[] = [];
  const options: OptionByCategory = {};

  for (const id of logicIds) {
    for (const cat of categories) {
      const match = cat.options?.find((opt) => opt.id === id);

      if (match) {
        if (!categoryIds.includes(cat.category)) {
          categoryIds.push(cat.category);
        }

        options[cat.category] = id;
        break;
      }
    }
  }

  return { categoryIds, options };
};

export const usePopulationsDraft = ({
  regime,
  customLogicCategories = [],
}: UsePopulationsDraftProps) => {
  const [baselineSelected, setBaselineSelected] = useState<string | null>(null);
  const [activeCategoryIds, setActiveCategoryIds] = useState<string[]>([]);
  const [optionByCategory, setOptionByCategory] = useState<OptionByCategory>(
    {}
  );
  const initialRef = useRef<InitialDraft>({
    population: null,
    customLogicIds: [],
  });
  const pendingLogicIdsRef = useRef<string[] | null>(null);

  useEffect(() => {
    const population = regime?.population ?? null;
    const logicIds = regime?.customLogicIds ?? [];

    setBaselineSelected(population);

    if (customLogicCategories.length > 0 && logicIds.length > 0) {
      const mapped = mapLogicIdsToCategories(logicIds, customLogicCategories);
      const resolvedIds = Object.values(mapped.options).filter(
        Boolean
      ) as string[];

      setActiveCategoryIds(mapped.categoryIds);
      setOptionByCategory(mapped.options);
      pendingLogicIdsRef.current = null;
      initialRef.current = {
        population,
        customLogicIds: resolvedIds,
      };
    } else {
      setActiveCategoryIds([]);
      setOptionByCategory({});
      pendingLogicIdsRef.current = logicIds.length > 0 ? logicIds : null;
      initialRef.current = { population, customLogicIds: [] };
    }
  }, [regime?.id]);

  useEffect(() => {
    if (!pendingLogicIdsRef.current || customLogicCategories.length === 0) return;

    const logicIds = pendingLogicIdsRef.current;
    const mapped = mapLogicIdsToCategories(logicIds, customLogicCategories);
    const resolvedIds = Object.values(mapped.options).filter(
      Boolean
    ) as string[];

    setActiveCategoryIds(mapped.categoryIds);
    setOptionByCategory(mapped.options);
    initialRef.current = { ...initialRef.current, customLogicIds: resolvedIds };
    pendingLogicIdsRef.current = null;
  }, [customLogicCategories]);

  const toggleBaseline = useCallback((value: string) => {
    setBaselineSelected(value);
  }, []);

  const toggleCategory = useCallback(
    (categoryName: string) => {
      const isRemoving = activeCategoryIds.includes(categoryName);

      setActiveCategoryIds((prev) =>
        isRemoving
          ? prev.filter((c) => c !== categoryName)
          : [...prev, categoryName]
      );

      if (isRemoving) {
        setOptionByCategory((opts) => {
          const next = { ...opts };

          delete next[categoryName];

          return next;
        });
      } else {
        const category = customLogicCategories.find(
          (c) => c.category === categoryName
        );

        if (category?.options?.length === 1) {
          setOptionByCategory((opts) => ({
            ...opts,
            [categoryName]: category.options![0].id,
          }));
        }
      }
    },
    [customLogicCategories, activeCategoryIds]
  );

  const removeCategory = useCallback((categoryName: string) => {
    setActiveCategoryIds((prev) => prev.filter((c) => c !== categoryName));
    setOptionByCategory((prev) => {
      const next = { ...prev };

      delete next[categoryName];

      return next;
    });
  }, []);

  const setOption = useCallback(
    (
      categoryName: string,
      optionId: string | number | null | undefined
    ) => {
      setOptionByCategory((prev) => ({
        ...prev,
        [categoryName]: optionId == null ? '' : String(optionId),
      }));
    },
    []
  );

  const activeCategories = useMemo(
    () =>
      activeCategoryIds
        .map((id) => customLogicCategories.find((c) => c.category === id))
        .filter((c): c is LogicCategory => Boolean(c)),
    [activeCategoryIds, customLogicCategories]
  );

  const [savedVersion, setSavedVersion] = useState(0);

  const customLogicIds = useMemo(
    () => Object.values(optionByCategory).filter(Boolean) as string[],
    [optionByCategory]
  );

  const hasChanges = useMemo(() => {
    const { population: initPop, customLogicIds: initLogic } = initialRef.current;

    return (
      baselineSelected !== initPop ||
      !arraysEqual(customLogicIds, initLogic)
    );
  }, [baselineSelected, customLogicIds, savedVersion]);

  const resetDraft = useCallback(() => {
    const { population, customLogicIds: ids } = initialRef.current;

    setBaselineSelected(population);

    if (ids.length > 0 && customLogicCategories.length > 0) {
      const mapped = mapLogicIdsToCategories(ids, customLogicCategories);

      setActiveCategoryIds(mapped.categoryIds);
      setOptionByCategory(mapped.options);
    } else {
      setActiveCategoryIds([]);
      setOptionByCategory({});
    }
  }, [customLogicCategories]);

  const markAsSaved = useCallback(() => {
    initialRef.current = {
      population: baselineSelected,
      customLogicIds: [...customLogicIds],
    };
    setSavedVersion((v) => v + 1);
  }, [baselineSelected, customLogicIds]);

  return {
    baselineSelected,
    toggleBaseline,
    activeCategoryIds,
    activeCategories,
    optionByCategory,
    toggleCategory,
    removeCategory,
    setOption,
    customLogicIds,
    hasChanges,
    resetDraft,
    markAsSaved,
  };
};
